import { arrayOf, maybe, number, string, tuple, values, debug, pointTest, V4 } from '@moleculeone/utils';

import * as Drawing from './const';
import { atomTest, bondTest, chiralTest, geomTest, molTest, orderTest } from './checks';
import { align, alignFlip } from './geometry';
import { ambiguousDetThreshold } from './drawing/warnings';
import { getBond, neighborsList, atomToNeighborsPairs, getSmallestCycles } from './selectors';
import { neighborsChiralityData, calcDepictedGeometry, chiralDeterminant } from './isomerism';

export function removeAtom(mol, atomIdx) {
  debug(molTest, mol);
  debug(string, atomIdx);

  if (!mol.atoms[atomIdx]) {
    throw new Error(`Atom to be removed ${atomIdx} does not exist.`);
  }

  for (const [bond] of neighborsList(mol, atomIdx)) {
    mol = removeBond(mol, bond.idx);
  }

  const atoms = { ...mol.atoms };
  delete atoms[atomIdx];

  const positions = { ...mol.positions };
  delete positions[atomIdx];

  return debug(molTest, { ...mol, atoms, positions });
}

export function removeBond(mol, bondIdx) {
  debug(molTest, mol);
  debug(string, bondIdx);

  if (!mol.bonds[bondIdx]) {
    throw new Error(`Bond to be removed ${bondIdx} does not exist.`);
  }

  const bond = mol.bonds[bondIdx];
  const bonds = { ...mol.bonds };
  delete bonds[bondIdx];

  let newMol = { ...mol, bonds };
  newMol = cleanupGeometry(newMol, bond.start);
  newMol = cleanupGeometry(newMol, bond.end);
  return debug(molTest, newMol);
}

export function addAtom(mol, position, element, charge) {
  // FIXME: make element test more strict?
  debug(tuple(molTest, pointTest, maybe(string), maybe(number)), [mol, position, element, charge]);

  const newId = mol.nextAtom.toString();
  const atoms = { ...mol.atoms };
  const newAtom = {
    idx: newId,
    element: element != null ? element : 'C',
    charge: charge != null ? charge : 0,
  };
  atoms[newId] = newAtom;

  const positions = { ...mol.positions };
  positions[newId] = position;
  const newState = {
    ...mol,
    atoms,
    positions,
    nextAtom: mol.nextAtom + 1,
  };

  debug(molTest, newState);
  debug(atomTest, newAtom);
  return [newState, newAtom];
}

export function addBond(mol, startAtomIdx, endAtomIdx, order, type, geometry) {
  debug(tuple(molTest, string, string), [mol, startAtomIdx, endAtomIdx]);
  debug(tuple(maybe(orderTest), maybe(chiralTest), maybe(geomTest)), [order, type, geometry]);

  if (startAtomIdx === endAtomIdx) {
    throw new Error(`Start equals end: ${startAtomIdx} === ${endAtomIdx}.`);
  }
  if (mol.atoms[startAtomIdx] == null) {
    throw new Error(`Start atom: ${startAtomIdx} is absent.`);
  }
  if (mol.atoms[endAtomIdx] == null) {
    throw new Error(`End atom: ${endAtomIdx} is absent.`);
  }
  if (getBond(mol, startAtomIdx, endAtomIdx) != null) {
    throw new Error('Duplicate bond.');
  }

  const newId = mol.nextBond.toString();
  const bonds = { ...mol.bonds };
  const newBond = {
    start: startAtomIdx,
    end: endAtomIdx,
    idx: newId,
    order: order != null ? order : 1,
  };
  if (type != null) {
    newBond.type = type;
  }
  if (geometry != null) {
    newBond.geometry = geometry;
  }
  bonds[newId] = newBond;

  mol = { ...mol, bonds, nextBond: mol.nextBond + 1 };
  mol = cleanupGeometry(mol, newBond.start);
  mol = cleanupGeometry(mol, newBond.end);

  debug(molTest, mol);
  debug(bondTest, newBond);
  return [mol, newBond];
}

export function editAtom(mol, atomIdx, element, charge) {
  debug(tuple(molTest, string, maybe(string), maybe(number)), [mol, atomIdx, element, charge]);
  const atoms = { ...mol.atoms };

  const mutation = {};
  if (element != null) {
    mutation.element = element;
  }
  if (charge != null) {
    mutation.charge = charge;
  }

  atoms[atomIdx] = { ...atoms[atomIdx], ...mutation };

  return debug(molTest, { ...mol, atoms });
}

export function editBond(mol, bondIdx, order, type, geometry) {
  debug(tuple(molTest, string), [mol, bondIdx]);
  debug(tuple(maybe(orderTest), maybe(chiralTest), maybe(geomTest)), [order, type, geometry]);

  const newBond = { ...mol.bonds[bondIdx] };
  if (order != null) {
    newBond.order = order;
  }
  if (type != null) {
    newBond.type = type;
  }
  if (geometry != null) {
    newBond.geometry = geometry;
  }
  if (newBond.order !== 1) {
    delete newBond.type;
  }
  if (newBond.order !== 2) {
    delete newBond.geometry;
  }
  const bonds = { ...mol.bonds };
  bonds[bondIdx] = newBond;

  return debug(molTest, { ...mol, bonds });
}

function cleanupGeometry(mol, atomIdx) {
  debug(molTest, mol);
  debug(string, atomIdx);

  const neighbors = neighborsList(mol, atomIdx);
  if (neighbors.length < 2 || neighbors.length > 3) {
    for (const [maybeDouble] of neighbors) {
      if (maybeDouble.geometry != null) {
        mol = clearGeometry(mol, maybeDouble.idx);
      }
    }
  } else if (neighbors.length === 2 || neighbors.length === 3) {
    for (const [maybeDouble] of neighbors) {
      if (maybeDouble.geometry != null) {
        const geometry = calcDepictedGeometry(mol, maybeDouble.idx);
        mol = editBond(mol, maybeDouble.idx, null, null, geometry);
      }
    }
  }
  return debug(molTest, mol);
}

export function clearGeometry(mol, bondIdx) {
  debug(molTest, mol);
  debug(string, bondIdx);

  const newBond = { ...mol.bonds[bondIdx] };
  delete newBond.geometry;
  const bonds = { ...mol.bonds };
  bonds[bondIdx] = newBond;

  return debug(molTest, { ...mol, bonds });
}

export function clearChirality(mol, bondIdx) {
  debug(molTest, mol);
  debug(string, bondIdx);

  const newBond = { ...mol.bonds[bondIdx] };
  delete newBond.type;
  const bonds = { ...mol.bonds };
  bonds[bondIdx] = newBond;

  return debug(molTest, { ...mol, bonds });
}

export function flipBondDirection(mol, bondIdx) {
  debug(molTest, mol);
  debug(string, bondIdx);

  const newBond = { ...mol.bonds[bondIdx] };
  const { start, end } = newBond;
  newBond.start = end;
  newBond.end = start;

  const bonds = { ...mol.bonds };
  bonds[bondIdx] = newBond;
  return debug(molTest, { ...mol, bonds });
}

export function flipType(type) {
  debug(chiralTest, type);
  return type === Drawing.WEDGE ? Drawing.DASHED : Drawing.WEDGE;
}

export function flipGeometry(geometry) {
  if (!geometry) {
    throw new Error("Geometry wasn't assigned.");
  }

  debug(geomTest, geometry);
  return geometry === Drawing.CIS ? Drawing.TRANS : Drawing.CIS;
}

export function setPositions(mol, newPositions, tryFlip = true) {
  const flipFun = tryFlip ? alignFlip : align;
  const alignedPositions = flipFun(newPositions, mol.positions);
  return setUnchangedPositions(mol, alignedPositions);
}

export function setUnchangedPositions(mol, newPositions) {
  debug(molTest, mol);
  debug(values(pointTest), newPositions);

  const atomsChiralBonds = {};
  for (const bond of Object.values(mol.bonds)) {
    if (bond.type === Drawing.WEDGE || bond.type === Drawing.DASHED) {
      if (atomsChiralBonds[bond.start] == null) {
        atomsChiralBonds[bond.start] = [];
      }
      atomsChiralBonds[bond.start].push(bond);
    }
  }

  const changes = {};
  for (const [atomIdx, chiralBonds] of Object.entries(atomsChiralBonds)) {
    const dataBefore = neighborsChiralityData(mol, atomIdx, mol.positions);
    const detBefore = chiralDeterminant(atomIdx, mol.positions[atomIdx], dataBefore);

    const dataAfter = neighborsChiralityData(mol, atomIdx, newPositions);
    const centerAfter = newPositions[atomIdx];
    let detAfter = chiralDeterminant(atomIdx, centerAfter, dataAfter);

    if (detBefore == null || detAfter == null) {
      continue;
    }

    if (chiralBonds.length > 1 && Math.abs(detAfter) < ambiguousDetThreshold) {
      // try to flip one of many chiral bonds to get bigger tetrahedron
      const flipEnd = dataAfter[chiralBonds[0].end];
      flipEnd.type = flipType(flipEnd.type);
      detAfter = chiralDeterminant(atomIdx, centerAfter, dataAfter);
      changes[chiralBonds[0].idx] = flipEnd.type;
    }

    // if chirality changed, flip all chiral bonds on that atom
    if (detAfter * detBefore < 0) {
      for (const bond of chiralBonds) {
        dataAfter[bond.end].type = flipType(dataAfter[bond.end].type);
        changes[bond.idx] = dataAfter[bond.end].type;
      }
    }
  }

  if (Object.keys(changes).length === 0) {
    return { ...mol, positions: newPositions };
  }

  const newBonds = { ...mol.bonds };
  for (const [bondIdx, type] of Object.entries(changes)) {
    newBonds[bondIdx] = { ...newBonds[bondIdx], type };
  }

  return debug(molTest, { ...mol, positions: newPositions, bonds: newBonds });
}

export function mergeMol(into, other, reposition = true) {
  debug(molTest, into);
  debug(molTest, other);

  let maxX = Object.values(into.positions).reduce((acc, n) => Math.max(n[0], acc), Number.NEGATIVE_INFINITY);
  const minX = Object.values(other.positions).reduce((acc, n) => Math.min(n[0], acc), Number.POSITIVE_INFINITY);
  const minAtomIdx = Object.keys(other.atoms).reduce((acc, n) => Math.min(acc, parseInt(n, 10)), 0);
  const minBondIdx = Object.keys(other.bonds).reduce((acc, n) => Math.min(acc, parseInt(n, 10)), 0);

  if (maxX === Number.NEGATIVE_INFINITY) {
    maxX = 0;
  }

  const atoms = { ...into.atoms };
  const bonds = { ...into.bonds };
  const positions = { ...into.positions };

  function newAtomIdx(old) {
    return (into.nextAtom + parseInt(old, 10) - minAtomIdx).toString();
  }

  function newBondIdx(old) {
    return (into.nextBond + parseInt(old, 10) - minBondIdx).toString();
  }

  for (const [idx, atom] of Object.entries(other.atoms)) {
    const newIdx = newAtomIdx(idx);
    atoms[newIdx] = { ...atom, idx: newIdx };
  }

  for (const [idx, bond] of Object.entries(other.bonds)) {
    const newIdx = newBondIdx(idx);
    bonds[newIdx] = {
      ...bond,
      idx: newIdx,
      start: newAtomIdx(bond.start),
      end: newAtomIdx(bond.end),
    };
  }

  const shift = V4.vect(100 + maxX - minX, 0);
  for (const [idx, pos] of Object.entries(other.positions)) {
    const newIdx = newAtomIdx(idx);
    positions[newIdx] = reposition ? V4.add(pos, shift) : pos;
  }

  return {
    newMol: debug(molTest, {
      atoms,
      bonds,
      positions,
      nextAtom: into.nextAtom + other.nextAtom - minAtomIdx,
      nextBond: into.nextBond + other.nextBond - minBondIdx,
    }),
    newAtomIdx,
    newBondIdx,
  };
}

export function getSubset(mol, atomIdxSubset) {
  debug(molTest, mol);
  debug(arrayOf(string), atomIdxSubset);

  let result = mol;
  const atomIdxSet = new Set(atomIdxSubset);
  for (const atomIdx of Object.keys(mol.atoms)) {
    if (!atomIdxSet.has(atomIdx)) {
      result = removeAtom(result, atomIdx);
    }
  }
  return result;
}

export function removeSubset(mol, atomIdxSubset) {
  let result = mol;
  for (const atomIdx of new Set(atomIdxSubset)) {
    result = removeAtom(result, atomIdx);
  }
  return result;
}

function sortBondsToWedge(bonds, bondToCycle, neighbors) {
  return [...bonds].sort(([b0, a0], [b1, a1]) => {
    const regularBond0 = b0.type == null;
    const regularBond1 = b1.type == null;
    const nonCyclic0 = bondToCycle[b0.idx] == null;
    const nonCyclic1 = bondToCycle[b1.idx] == null;

    // always pick unstyled (nonwedge and nondashed bonds) first
    // then, always pick acyclic bond first
    // then, always pick bond to more connected atom
    // last one is a precaution so we don't make drawing
    // wedges and dashes impossible for some compounds
    // with lots of neighbouring chiral atoms, but that's
    // making some of the drawings ugly, and should be
    // loosened - we should just pick bonds to achiral
    // atoms first, then to ones with lesser number of bonds
    if (regularBond0 !== regularBond1) {
      return regularBond0 ? -1 : 1;
    }
    if (nonCyclic0 !== nonCyclic1) {
      return nonCyclic0 ? -1 : 1;
    }
    return neighbors[a0.idx].length - neighbors[a1.idx].length;
  });
}

export function decodeChirality(mol) {
  let neighbors = atomToNeighborsPairs(mol);
  const { bondToCycle } = getSmallestCycles(mol);

  const chiralAtoms = [];
  for (const atom of Object.values(mol.atoms)) {
    if (atom.chirality != null && atom.chirality !== 'NONE') {
      chiralAtoms.push(atom.idx);
    }
  }

  chiralAtoms.sort(
    // i guess we don't have to care about
    // integer over/underflow in js
    (a, b) => neighbors[a].length - neighbors[b].length
  );

  const result = { ...mol, bonds: { ...mol.bonds }, atoms: { ...mol.atoms } };
  for (const atomIdx of chiralAtoms) {
    const oldBond = sortBondsToWedge(neighbors[atomIdx], bondToCycle, neighbors)[0][0];
    let { start, end } = oldBond;
    if (start !== atomIdx) {
      [start, end] = [end, start];
    }
    result.bonds[oldBond.idx] = {
      ...mol.bonds[oldBond.idx],
      type: Drawing.WEDGE,
      start,
      end,
    };

    const neighborsData = neighborsChiralityData(result, atomIdx, result.positions);
    const det = chiralDeterminant(atomIdx, result.positions[atomIdx], neighborsData);
    if (det < 0 !== (mol.atoms[atomIdx].chirality === 'EVEN')) {
      result.bonds[oldBond.idx].type = Drawing.DASHED;
    }

    result.atoms[atomIdx] = { ...mol.atoms[atomIdx] };
    neighbors = atomToNeighborsPairs(result);
  }
  for (const atom of Object.values(result.atoms)) {
    delete atom.chirality;
  }
  return result;
}

export function encodeChirality(mol) {
  const chiralAtoms = {};
  for (const bond of Object.values(mol.bonds)) {
    if (bond.type === Drawing.WEDGE || bond.type === Drawing.DASHED) {
      chiralAtoms[bond.start] = true;
    }
  }
  if (Object.keys(chiralAtoms).length === 0) {
    return mol;
  }

  const result = { ...mol, atoms: { ...mol.atoms } };
  for (const idx of Object.keys(chiralAtoms)) {
    const neighborsData = neighborsChiralityData(mol, idx, mol.positions);
    const det = chiralDeterminant(idx, mol.positions[idx], neighborsData);
    let chirality = null;
    if (det < 0) {
      chirality = 'EVEN';
    }
    if (det > 0) {
      chirality = 'ODD';
    }
    result.atoms[idx] = { ...mol.atoms[idx], chirality };
  }
  return result;
}
